// Event payload builder for the Fintepla frontend analytics pipeline.
//
// IMPORTANT:
// The backend ingest contract expects a flat payload (FrontendEventIngest):
//   - event_type
//   - analytics_session_id
//   - client_action_id
//   - optional page/device/rich-message context fields
//   - optional metadata (free-form)
//
// We still keep a few internal fields for debugging in `metadata.raw_payload`
// so backend can store a full copy without coupling on a nested schema.

import { getAnalyticsSessionId, getAnonymousUserId } from './ids.js'

/**
 * @typedef {'page_viewed'
 *  | 'chat_widget_loaded'
 *  | 'chat_launcher_clicked'
 *  | 'chat_widget_opened'
 *  | 'chat_widget_closed'
 *  | 'conversation_start_requested'
 *  | 'conversation_restarted'
 *  | 'custom_speak_to_person_clicked'
 *  | 'rich_message_button_clicked'
 *  | 'external_link_clicked'
 *  | 'phone_cta_clicked'} FrontendAnalyticsEventType
 */

/**
 * @typedef {object} FrontendEventIngest
 * @property {string} event_type
 * @property {string} analytics_session_id
 * @property {string} client_action_id
 * @property {string} [conversation_id]
 * @property {string} [user_id]
 * @property {string} [page_url]
 * @property {string} [page_path]
 * @property {string} [page_title]
 * @property {string} [referrer]
 * @property {string} [user_agent]
 * @property {'mobile'|'tablet'|'desktop'|'unknown'} [device_type]
 * @property {string} [button_id]
 * @property {string} [button_label]
 * @property {string} [template_id]
 * @property {string} [message_id]
 * @property {object} [metadata]
 */

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
]

function safe(fn, fallback) {
  try {
    return fn()
  } catch {
    return fallback
  }
}

function parseCampaign(search) {
  const out = {}
  const q = safe(
    () => new URLSearchParams(search || ''),
    new URLSearchParams(''),
  )
  for (const key of UTM_KEYS) {
    const v = q.get(key)
    if (v) out[key] = v
  }
  return Object.keys(out).length ? out : undefined
}

function detectDeviceType(ua) {
  if (!ua) return 'unknown'
  if (/iPad|Tablet|(Android(?!.*Mobile))/i.test(ua)) return 'tablet'
  if (/Mobi|iPhone|Android|IEMobile|BlackBerry/i.test(ua)) return 'mobile'
  return 'desktop'
}

function buildContext() {
  if (typeof window === 'undefined') return undefined
  const loc = safe(() => window.location, null)
  const doc = safe(() => window.document, null)
  const nav = safe(() => window.navigator, null)
  const ctx = {}
  if (loc) {
    if (loc.href) ctx.page_url = loc.href
    if (loc.pathname) ctx.page_path = loc.pathname
  }
  if (doc) {
    if (doc.title) ctx.page_title = doc.title
    if (doc.referrer) ctx.referrer = doc.referrer
  }
  const campaign = parseCampaign(loc?.search)
  if (campaign) ctx.campaign = campaign
  if (nav?.userAgent) {
    ctx.user_agent = nav.userAgent
    ctx.device_type = detectDeviceType(nav.userAgent)
  }
  return Object.keys(ctx).length ? ctx : undefined
}

/**
 * Build a normalized payload for the backend ingest endpoint.
 *
 * @param {FrontendAnalyticsEventType} eventType
 * @param {{ environment?: string, correlation?: object, message?: object, metadata?: object, context?: object }} [opts]
 * @param {string} clientActionId
 * @returns {FrontendEventIngest}
 */
export function buildEvent(eventType, opts = {}, clientActionId) {
  const context = { ...(buildContext() || {}), ...(opts.context || {}) }
  const message = opts.message && Object.keys(opts.message).length ? { ...opts.message } : {}
  const correlation =
    opts.correlation && Object.keys(opts.correlation).length ? { ...opts.correlation } : {}

  const out = {
    event_type: eventType,
    analytics_session_id: getAnalyticsSessionId(),
    client_action_id: clientActionId,
  }

  const userId = getAnonymousUserId()
  if (userId) out.user_id = userId

  if (context.page_url) out.page_url = context.page_url
  if (context.page_path) out.page_path = context.page_path
  if (context.page_title) out.page_title = context.page_title
  if (context.referrer) out.referrer = context.referrer
  if (context.user_agent) out.user_agent = context.user_agent
  if (context.device_type) out.device_type = context.device_type

  if (typeof correlation.conversation_id === 'string' && correlation.conversation_id) {
    out.conversation_id = correlation.conversation_id
  }

  if (typeof message.button_id === 'string' && message.button_id) out.button_id = message.button_id
  if (typeof message.button_label === 'string' && message.button_label) {
    out.button_label = message.button_label
  }
  if (typeof message.template_id === 'string' && message.template_id) out.template_id = message.template_id
  if (typeof message.message_id === 'string' && message.message_id) out.message_id = message.message_id

  const metadata = opts.metadata && Object.keys(opts.metadata).length ? { ...opts.metadata } : {}
  if (!('raw_payload' in metadata)) {
    metadata.raw_payload = {
      ...(Object.keys(context).length ? { context } : {}),
      ...(Object.keys(message).length ? { message } : {}),
      ...(Object.keys(correlation).length ? { correlation } : {}),
      ...(opts.environment ? { environment: opts.environment } : {}),
    }
  }

  if (Object.keys(metadata).length) out.metadata = metadata
  return out
}

// `EVENT_VERSION` intentionally removed from the public payload; the backend
// is the source of truth for schema evolution.

